import { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, ShieldCheck, Lock, PlayCircle, SpeakerHigh, Scales as Scale } from "@phosphor-icons/react";
import { SiteNav } from "../components/SiteNav";
import { RealisticPhone, RealisticLaptop } from '../components/DeviceFrames';
import SiteFooter from '../components/SiteFooter';
import { QuoteWall } from '../components/QuoteWall';
import BetaOpenModal from '../components/BetaOpenModal';

const API = process.env.REACT_APP_BACKEND_URL;

const INK = '#2A1E5C';
const INK2 = '#5D5A6B';
const GOLD = '#F5CB5C';
const RED = '#DE3C4B';
const GREEN = '#3E8E4E';
const CREAM = '#FDFBF7';
const PAPER = '#FAF1D8';
const BEIGE = '#F0E6DD';


/* Structured data, tells search engines this is a subscription SaaS product */
const HOME_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  name: 'Payday Relay',
  applicationCategory: 'FinanceApplication',
  applicationSubCategory: 'Income and cash-flow planning for self-employed earners',
  operatingSystem: 'Web, iOS, Android',
  url: 'https://paydayrelay.com',
  description:
    'Payday Relay turns unpredictable 1099 income into one weekly finish line, with tax and paid-time-off set-asides that sync automatically to your own Google Sheet.',
  author: { '@type': 'Person', name: 'Shari Dove Shepard' },
  founder: { '@type': 'Person', name: 'Shari Dove Shepard' },
  publisher: { '@type': 'Organization', name: 'Earthwise Digital LLC', url: 'https://paydayrelay.com' },
  offers: [
    {
      '@type': 'Offer',
      name: 'Monthly Pass',
      price: '19.00',
      priceCurrency: 'USD',
      category: 'subscription',
      url: 'https://paydayrelay.com/start/season-pass',
    },
    {
      '@type': 'Offer',
      name: 'Annual Season Pass',
      price: '119.00',
      priceCurrency: 'USD',
      category: 'subscription',
      url: 'https://paydayrelay.com/start/season-pass?plan=annual',
    },
    {
      '@type': 'Offer',
      name: 'Founding Members Beta',
      description:
        'Inaugural beta season. Participants start free with no card and receive 50% off for life in exchange for product feedback.',
      price: '9.50',
      priceCurrency: 'USD',
      category: 'subscription',
      url: 'https://paydayrelay.com/?beta=1',
    },
  ],
};

// Scroll reveal (uses pr-reveal classes from index.css)
const Reveal = ({ children, className = '', delay = 0 }) => {
  const [ref, setRef] = useState(null);
  const [shown, setShown] = useState(false);
  useEffect(() => {
    if (!ref) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); obs.disconnect(); }
    }, { threshold: 0.12 });
    obs.observe(ref);
    return () => obs.disconnect();
  }, [ref]);
  return (
    <div ref={setRef} style={{ transitionDelay: `${delay}ms` }} className={`pr-reveal ${shown ? 'pr-reveal-in' : ''} ${className}`}>
      {children}
    </div>
  );
};

/* ================= Hero winding roads (animated dashes) ================= */
const HeroRoads = () => (
  <svg
    className="absolute inset-y-0 right-0 w-[58%] h-full pointer-events-none hidden md:block"
    viewBox="0 0 520 640"
    fill="none"
    preserveAspectRatio="xMidYMid slice"
    aria-hidden="true"
  >
    <path d="M 470 -30 C 470 130, 300 150, 350 280 C 400 410, 560 380, 520 520 C 495 615, 380 620, 360 700"
      stroke="rgba(255,255,255,0.09)" strokeWidth="34" strokeLinecap="round" />
    <path d="M 470 -30 C 470 130, 300 150, 350 280 C 400 410, 560 380, 520 520 C 495 615, 380 620, 360 700"
      stroke="#C9BE9E" strokeWidth="2.5" strokeDasharray="14 12" strokeLinecap="round" opacity="0.8" className="pr-road-dash" />
    <path d="M 250 720 C 240 590, 380 560, 340 440 C 305 335, 170 350, 190 230"
      stroke="rgba(255,255,255,0.06)" strokeWidth="28" strokeLinecap="round" />
    <path d="M 250 720 C 240 590, 380 560, 340 440 C 305 335, 170 350, 190 230"
      stroke="#C9BE9E" strokeWidth="2" strokeDasharray="12 12" strokeLinecap="round" opacity="0.5" className="pr-road-dash-slow" />
  </svg>
);

/* ================= The 3 pieces of the system (map + sheet + app) ================= */
const SYSTEM_ITEMS = [
  {
    n: '1', title: 'Your Target',
    desc: 'The one weekly number that funds your whole year.',
    img: '/images/home-mockup-map.png', alt: 'Your relay targets, weekly target and 12-month path',
    frame: 'flat',
  },
  {
    n: '2', title: 'Your App',
    desc: 'Log income from your phone and watch the week go from red to green.',
    desc2: 'Ask Caddy for a read on your week and a sensible next step.',
    img: '/images/home-mockup-app-v2.png', alt: "The Payday Relay app, what's left this week on your phone",
    frame: 'phone',
  },
  {
    n: '3', title: 'Your Sheet',
    desc: 'Your own Google Sheet that syncs automatically and is yours to keep.',
    img: '/images/home-mockup-sheet.png', alt: 'Your Payday Relay Google Sheet, weekly relays, deductions, and allocations',
    frame: 'laptop',
  },
];

/* Mockups stand on their own. No cards. Band height keeps the "+" signs centered on them. */
const SystemMockup = ({ item, delay }) => (
  <Reveal delay={delay} className="flex-1 min-w-0 w-full">
    <div className="flex flex-col items-center" data-testid={`system-item-${item.n}`}>
      <div className="flex items-center justify-center w-full lg:h-[320px]">
        {item.frame === 'phone' ? (
          <RealisticPhone src={item.img} alt={item.alt} className="w-[150px] sm:w-[168px]" camera="punch" radius={2.1} />
        ) : item.frame === 'laptop' ? (
          <RealisticLaptop src={item.img} alt={item.alt} className="w-full max-w-[360px]" />
        ) : (
          <img src={item.img} alt={item.alt} className="w-full max-w-[410px] h-auto block" loading="lazy" />
        )}
      </div>
      <div className="mt-11 sm:mt-14 max-w-[340px] text-center">
        <span
          className="relative block text-4xl sm:text-5xl font-bold mb-3 leading-none"
          style={{
            color: 'rgba(201,190,158,0.85)',
            fontFamily: "'Fraunces', Georgia, serif",
          }}
          aria-hidden="true"
        >
          {item.n}
        </span>
        <h3 className="text-xl font-bold leading-snug mb-2" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }}>{item.title}</h3>
        <p className="text-base leading-relaxed" style={{ color: 'rgba(255,255,255,0.78)' }}>{item.desc}</p>
        {item.desc2 && (
          <p className="text-sm leading-relaxed mt-2" style={{ color: 'rgba(255,255,255,0.6)' }} data-testid="system-item-2-caddy-line">{item.desc2}</p>
        )}
      </div>
    </div>
  </Reveal>
);

const PlusSign = () => (
  <div className="flex justify-center items-center flex-shrink-0 py-2 lg:py-0 lg:h-[320px]" aria-hidden="true">
    <span
      className="w-11 h-11 rounded-full flex items-center justify-center text-2xl font-bold flex-shrink-0"
      style={{ background: 'rgba(245,203,92,0.14)', color: GOLD, border: '2px solid rgba(245,203,92,0.35)' }}
    >
      +
    </span>
  </div>
);

/* Loud gold CTA, used for the mid-page "pop" calls to action */
const PopCta = ({ to, label, testId }) => (
  <Link
    to={to}
    className="group inline-flex items-center justify-center gap-2.5 px-9 py-4 rounded-full font-bold text-base sm:text-lg transition-transform hover:scale-[1.04]"
    style={{ background: GOLD, color: INK, boxShadow: '0 0 0 6px rgba(245,203,92,0.16), 0 18px 36px rgba(0,0,0,0.35)' }}
    data-testid={testId}
  >
    {label}
    <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
  </Link>
);

/* Winding race road that bleeds off both edges, baton rides it at a tilt */
const SYSTEM_ROAD = 'M -140 82 C 60 14, 250 12, 420 62 C 580 108, 700 112, 860 60 C 1000 14, 1140 16, 1340 76';

const RaceStrip = () => {
  const [still] = useState(() => {
    try { return window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch { return false; }
  });
  return (
    <div
      className="relative w-screen left-1/2 -translate-x-1/2 mt-10 sm:mt-14 h-[130px] sm:h-[175px]"
      aria-hidden="true"
      data-testid="home-system-racetrack"
    >
      <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1200 120" preserveAspectRatio="xMidYMid slice" fill="none">
        <path d={SYSTEM_ROAD} stroke="rgba(245,203,92,0.18)" strokeWidth="50" strokeLinecap="round" />
        <path d={SYSTEM_ROAD} stroke="#221847" strokeWidth="42" strokeLinecap="round" />
        <path
          id="pr-system-road"
          d={SYSTEM_ROAD}
          stroke="rgba(243,234,211,0.72)"
          strokeWidth="3.5"
          strokeDasharray="26 26"
          strokeLinecap="round"
          className={still ? '' : 'pr-road-dash'}
        />
        {/* baton, tilted off the center line */}
        <g>
          <image href="/images/brand-icon.png" x="-26" y="-26" width="52" height="52" transform="rotate(-12)" />
          {!still && (
            <animateMotion dur="13s" repeatCount="indefinite" rotate="auto">
              <mpath href="#pr-system-road" xlinkHref="#pr-system-road" />
            </animateMotion>
          )}
        </g>
        {/* coin trailing behind */}
        <g>
          <circle r="11" fill="#F5CB5C" stroke="#C9A227" strokeWidth="2" />
          <text y="4.5" textAnchor="middle" fontSize="13" fontWeight="700" fill="#2A1E5C" fontFamily="Georgia, serif">$</text>
          {!still && (
            <animateMotion dur="19s" begin="2.5s" repeatCount="indefinite" rotate="0">
              <mpath href="#pr-system-road" xlinkHref="#pr-system-road" />
            </animateMotion>
          )}
        </g>
      </svg>
    </div>
  );
};

/* ================= Page ================= */
export default function HomePage() {
  const [showBetaModal, setShowBetaModal] = useState(false);
  const [betaFull, setBetaFull] = useState(false);
  const [seatInfo, setSeatInfo] = useState(null);
  const [plan, setPlan] = useState('monthly');
  const [member, setMember] = useState(false);
  const [videoActivated, setVideoActivated] = useState(false);
  const videoRef = useRef(null);
  const videoWrapRef = useRef(null);
  const location = useLocation();

  useEffect(() => {
    if (new URLSearchParams(location.search).get('beta') === '1') {
      setShowBetaModal(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }, [location]);

  useEffect(() => {
    const wrap = videoWrapRef.current;
    const video = videoRef.current;
    if (!wrap || !video) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (videoActivated) return;
        if (entry.isIntersecting) {
          video.muted = true;
          video.play().catch(() => {});
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(wrap);
    return () => observer.disconnect();
  }, [videoActivated]);

  const handleActivateVideo = () => {
    const video = videoRef.current;
    if (!video) return;
    video.loop = false;
    video.muted = false;
    video.currentTime = 0;
    video.controls = true;
    video.play().catch(() => {});
    setVideoActivated(true);
  };

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth' }), 120);
    }
  }, [location.hash]);

  useEffect(() => {
    fetch(`${API}/api/auth/me`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => setMember(!!(d && d.email)))
      .catch(() => {});
    fetch(`${API}/api/beta-waitlist/status`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) { setBetaFull(!!d.full); setSeatInfo({ count: d.count, cap: d.cap }); } })
      .catch(() => {});
  }, []);

  return (
    <div className="min-h-screen" style={{ background: CREAM }}>
      <style>{`
        @keyframes pr-road-dash { to { stroke-dashoffset: -104; } }
        .pr-road-dash { animation: pr-road-dash 7s linear infinite; }
        .pr-road-dash-slow { animation: pr-road-dash 11s linear infinite reverse; }
        @keyframes pr-phone-drift {
          0%, 100% { transform: translateY(0) rotate(7deg); }
          50% { transform: translateY(-12px) rotate(7deg); }
        }
        .pr-phone-drift { animation: pr-phone-drift 7s ease-in-out infinite; }
        @keyframes pr-phone-drift-alt {
          0%, 100% { transform: translateY(0) rotate(9deg); }
          50% { transform: translateY(-10px) rotate(9deg); }
        }
        .pr-phone-drift-alt { animation: pr-phone-drift-alt 6.5s ease-in-out infinite; }
        @keyframes pr-quote-float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .pr-quote-float { animation: pr-quote-float 5s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .pr-road-dash, .pr-road-dash-slow, .pr-phone-drift, .pr-phone-drift-alt, .pr-quote-float { animation: none; }
        }
      `}</style>

      {/* Nav */}
      <SiteNav onBetaClick={() => setShowBetaModal(true)} betaFull={betaFull} />

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(HOME_SCHEMA) }} />

      <BetaOpenModal isOpen={showBetaModal} onClose={() => setShowBetaModal(false)} full={betaFull} seatInfo={seatInfo} />

      {/* ============ HERO ============ */}
      <section className="relative overflow-hidden" style={{ background: BEIGE }} data-testid="home-hero">

        {/* Flat-lay desk background (no device), full-bleed. Phone + copy layer on top. */}
        <div className="absolute inset-0 z-0">
          <img
            src="/images/hero-bg.jpg"
            alt=""
            aria-hidden="true"
            className="w-full h-full object-cover object-center"
            data-testid="home-hero-bg"
          />
          {/* Left cream wash keeps the navy copy legible */}
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to right, rgba(245,238,221,0.94) 0%, rgba(243,235,217,0.78) 30%, rgba(241,231,211,0.3) 56%, rgba(241,231,211,0) 76%)' }}
            aria-hidden="true"
          />
        </div>

        <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pt-8 sm:pt-10 lg:py-12">
          <div className="lg:grid lg:grid-cols-[1.08fr_0.92fr] lg:gap-10 lg:items-center lg:min-h-[500px]">

            {/* Left column: copy */}
            <div className="max-w-2xl">
              <p className="text-base sm:text-lg font-bold uppercase tracking-widest mb-6" style={{ color: RED }} data-testid="home-hero-eyebrow">
                You didn't quit the 9-to-5 to work 24/7
              </p>
              <h1 className="text-4xl sm:text-5xl lg:text-[3.1rem] leading-[1.12] mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="home-hero-headline">
                See how far you can go<br className="hidden lg:block" /> when your income has<br className="hidden lg:block" /> a finish line
              </h1>
              <p className="text-base sm:text-lg leading-relaxed mb-10 max-w-md lg:max-w-xl lg:text-balance" style={{ color: 'rgba(42,30,92,0.74)' }} data-testid="home-hero-subhead">
                Turn unstructured income into a weekly target, set clearer goals for your money, and know when to call it a day.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  to="/start/free-sprint"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-base whitespace-nowrap transition-transform hover:scale-[1.03] shadow-lg"
                  style={{ background: GOLD, color: INK }}
                  data-testid="home-hero-cta"
                >
                  Start Your Free Sprint <ArrowRight className="w-5 h-5" />
                </Link>
                <a
                  href="#see-it-work"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-bold text-base whitespace-nowrap border-2 transition-colors hover:bg-[#2A1E5C]/5"
                  style={{ borderColor: 'rgba(42,30,92,0.3)', color: INK }}
                  data-testid="home-hero-secondary-cta"
                >
                  See How It Works <PlayCircle className="w-5 h-5" />
                </a>
              </div>
            </div>

            {/* Right column: CSS phone mockup with the real screenshot (desktop/tablet) */}
            <div className="hidden lg:flex justify-start items-center">
              <div className="relative" style={{ transform: 'translateX(-28px) rotate(6.5deg)' }}>
                <div
                  className="relative rounded-[2.4rem] p-[8px]"
                  style={{ background: '#0b0b11', width: '250px', boxShadow: '0 48px 64px -18px rgba(31,22,64,0.55), 0 18px 30px -12px rgba(0,0,0,0.38)' }}
                  data-testid="home-hero-phone"
                >
                  <img
                    src="/images/hero-app-screenshot.webp"
                    alt="The Payday Relay app showing this week's income target"
                    className="block w-full h-auto rounded-[1.85rem]"
                    data-testid="home-hero-phone-screenshot"
                  />
                  <span className="absolute right-[-3px] top-[118px] w-[3px] h-16 rounded-r-md" style={{ background: '#0b0b11' }} aria-hidden="true" />
                </div>
              </div>
            </div>
          </div>

          {/* Mobile: phone mockup sits below the copy, centered */}
          <div className="lg:hidden mt-12 mb-4 flex justify-center">
            <div className="relative" style={{ transform: 'rotate(4.5deg)' }}>
              <div
                className="relative rounded-[2.4rem] p-[8px]"
                style={{ background: '#0b0b11', width: '250px', boxShadow: '0 26px 54px -16px rgba(42,30,92,0.4)' }}
                data-testid="home-hero-phone-mobile"
              >
                <img
                  src="/images/hero-app-screenshot.webp"
                  alt="The Payday Relay app showing this week's income target"
                  className="block w-full h-auto rounded-[1.85rem]"
                  data-testid="home-hero-phone-screenshot-mobile"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ THE INDEPENDENT WORK TRAP, problem + philosophy ============ */}
      <section className="py-16 sm:py-24 px-5 sm:px-8" style={{ background: INK }} data-testid="home-trap">
        <div className="max-w-6xl mx-auto">
          <Reveal className="max-w-3xl">
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: CREAM }}>
              The Independent Work Trap
            </h2>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-x-10 md:gap-x-14 gap-y-0 mt-12 sm:mt-14 md:[grid-template-rows:auto_auto_1fr]">
            <Reveal className="md:grid md:row-span-3 md:[grid-template-rows:subgrid]">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: GOLD }}>The problem</p>
              <h3 className="text-2xl sm:text-3xl leading-snug mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: CREAM }} data-testid="home-problem-headline">
                When no one else defines the workday, it's hard to know when you're done.
              </h3>
              <p className="text-base sm:text-lg leading-relaxed" style={{ color: 'rgba(253,251,247,0.78)' }}>
                Income comes in pieces, and the workday has no built-in edges. It's hard to
                know whether you've earned enough or whether you can step away without falling
                behind.
              </p>
            </Reveal>

            <Reveal delay={120} className="md:grid md:row-span-3 md:[grid-template-rows:subgrid]">
              <p className="text-[11px] font-bold uppercase tracking-[0.22em] mb-4" style={{ color: GOLD }}>The fix</p>
              <h3 className="text-2xl sm:text-3xl leading-snug mb-5" style={{ fontFamily: "'Fraunces', Georgia, serif", color: CREAM }} data-testid="home-philosophy-headline">
                Structure around money, time, rest, and agency.
              </h3>
              <p className="text-base sm:text-lg leading-relaxed" style={{ color: 'rgba(253,251,247,0.78)' }}>
                Payday Relay turns your bigger money goals into one clear weekly target. See your
                progress as you earn, make room for taxes and time off, and know when you've done
                enough for the week and the day.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ============ EXPLAINER VIDEO ============ */}
      <section id="see-it-work" className="scroll-mt-20 py-16 sm:py-20 px-5 sm:px-8" style={{ background: CREAM }} data-testid="home-explainer-video">
        <div className="max-w-3xl mx-auto">
          <Reveal className="text-center mb-8">
            <h2 className="text-3xl sm:text-4xl font-medium leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
              Payday Relay at Work
            </h2>
          </Reveal>
          <Reveal delay={120}>
            <div
              ref={videoWrapRef}
              className="relative w-full rounded-[1.5rem] overflow-hidden"
              style={{ aspectRatio: '16 / 9', background: INK, boxShadow: '0 24px 50px rgba(42,30,92,0.25)' }}
              data-testid="home-video-placeholder"
            >
              <video
                ref={videoRef}
                className="absolute inset-0 w-full h-full object-cover"
                playsInline
                preload="metadata"
                muted
                loop
                data-testid="home-explainer-video-player"
              >
                <source src="https://customer-assets-rejwkqb3.emergentagent.net/wingman/5cc0ad01-9dd9-4016-b0aa-c22bc26dbf56/attachments/cc7eb30f725d41c49e26a63da2c99c29_payday_relay_explainer_v6.mp4" type="video/mp4" />
                <track kind="captions" src="/captions/payday-relay-explainer.vtt" srcLang="en" label="English" default />
              </video>
              {!videoActivated && (
                <button
                  type="button"
                  onClick={handleActivateVideo}
                  aria-label="Play explainer video with sound"
                  className="absolute inset-0 w-full h-full flex flex-col items-center justify-center gap-2 bg-black/0 hover:bg-black/15 transition-colors"
                  data-testid="home-video-play-button"
                >
                  <span className="w-16 h-16 rounded-full flex items-center justify-center transition-transform hover:scale-110" style={{ background: 'rgba(245,203,92,0.92)' }}>
                    <SpeakerHigh className="w-8 h-8" style={{ color: INK }} weight="fill" />
                  </span>
                </button>
              )}
            </div>
          </Reveal>
          <Reveal delay={220} className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-10">
            <PopCta to="/start/free-sprint" label="Start Today" testId="home-video-start-cta" />
          </Reveal>
        </div>
      </section>

      {/* ============ EVERYTHING YOU NEED, road behind the cards ============ */}
      <section className="relative overflow-hidden px-5 sm:px-8 pt-10 pb-16 sm:pb-20" style={{ background: INK }} data-testid="home-features">
        <div className="max-w-6xl mx-auto">
          <Reveal className="text-center mb-14">
            <p className="text-sm font-bold mb-2" style={{ color: GOLD }}>
              Included with Your Subscription
            </p>
            <h2 className="text-3xl sm:text-4xl mb-4" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }}>
              Everything You Need To Run Your Race
            </h2>
            <p className="text-base max-w-2xl mx-auto" style={{ color: 'rgba(255,255,255,0.75)' }}>
              Your target, your app, and your Google Sheet work together as one system. You'll lean on each one, even if you live in just one.
            </p>
          </Reveal>

          <div className="flex flex-col lg:flex-row lg:items-start gap-8 lg:gap-5">
            {SYSTEM_ITEMS.flatMap((item, i) => {
              const nodes = [<SystemMockup key={item.n} item={item} delay={i * 120} />];
              if (i < SYSTEM_ITEMS.length - 1) nodes.push(<PlusSign key={`plus-${item.n}`} />);
              return nodes;
            })}
          </div>

          <Reveal delay={320} className="text-center mt-12">
            <PopCta to="/start/free-sprint" label="Start Today" testId="home-system-pop-cta" />
          </Reveal>
        </div>
      </section>

      {/* ============ PARTNER STRIP + VETTED TESTIMONIAL ============ */}
      <section
        className="relative px-5 sm:px-8 py-14 sm:py-16"
        style={{
          background: '#F4F5F8',
          backgroundImage: 'radial-gradient(rgba(42,30,92,0.045) 1px, transparent 1px)',
          backgroundSize: '18px 18px',
        }}
        data-testid="home-tennant"
      >
        <div className="max-w-3xl mx-auto">
          {/* Partnership — slim unframed strip */}
          <Reveal className="mb-10 sm:mb-12">
            <div className="flex flex-col items-center justify-center gap-3 text-center" data-testid="home-partner">
              <span className="text-xs sm:text-sm font-bold uppercase tracking-[0.18em]" style={{ color: RED }} data-testid="home-partner-eyebrow">
                Proud Partner
              </span>
              <a
                href="https://zccounting.com"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-block flex-shrink-0 transition-transform hover:-translate-y-0.5"
                data-testid="home-partner-logo-link"
              >
                <img
                  src="/images/zccounting-logo.png"
                  alt="Zccounting"
                  loading="lazy"
                  className="h-20 sm:h-[104px] w-auto"
                  data-testid="home-partner-logo"
                />
              </a>
              <p className="text-base sm:text-lg leading-snug max-w-xl" style={{ color: 'rgba(42,30,92,0.78)' }} data-testid="home-partner-body">
                <span className="font-bold" style={{ color: INK }} data-testid="home-partner-heading">Now teaming up with Zccounting </span>
                to connect you with expert bookkeeping and accounting support when your business needs it.
              </p>
            </div>
          </Reveal>

          {/* Vetted + Derek Tennant testimonial — the main event */}
          <Reveal delay={150}>
            <figure className="rounded-3xl bg-white border border-[rgba(42,30,92,0.07)] shadow-[0_18px_50px_-22px_rgba(42,30,92,0.4)] px-7 py-9 sm:px-12 sm:py-11" data-testid="home-testimonial">
              <div className="text-center mb-7">
                <div className="inline-flex items-center justify-center w-11 h-11 rounded-full mb-3" style={{ background: 'rgba(222,60,75,0.08)', border: '2px solid rgba(222,60,75,0.25)' }}>
                  <ShieldCheck className="w-5 h-5" style={{ color: RED }} strokeWidth={2.25} />
                </div>
                <h2 className="text-2xl sm:text-3xl font-medium leading-snug" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>
                  Vetted by tax and finance pros who support 1099 earners.
                </h2>
              </div>
              <blockquote className="text-base sm:text-lg leading-relaxed italic mb-6" style={{ color: INK2 }}>
                Thank you for an amazing, simple resource to help self-employed people calculate the estimated
                payments required each quarter. This is a big challenge to do without a framework. I also love the
                way this tool helps set stretch goals and track progress towards them. What we measure is a key to
                getting the results we want.
              </blockquote>
              <figcaption className="flex items-center justify-end gap-3">
                <div className="text-right">
                  <p className="text-sm font-bold" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }}>Derek Tennant</p>
                  <p className="text-[11px]" style={{ color: '#8B7E6A' }}>
                    Enrolled Agent, <a href="https://esoterax.com" target="_blank" rel="noopener noreferrer" className="underline hover:opacity-80">esoterax.com</a>
                  </p>
                </div>
                <img
                  src="/images/screenshot-2026-06-05-at-6-12-17-pm.webp"
                  alt="Derek Tennant"
                  className="w-14 h-14 rounded-full object-cover flex-shrink-0"
                  style={{ border: `2px solid ${GOLD}` }}
                  loading="lazy"
                />
              </figcaption>
            </figure>
          </Reveal>

          <Reveal delay={260} className="flex flex-col sm:flex-row items-stretch justify-center gap-3 mt-10 sm:mt-12 max-w-2xl mx-auto">
            <Link
              to="/compare"
              className="group flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl shadow-md"
              style={{ background: INK, color: '#FFFFFF' }}
              data-testid="home-compare-cta"
            >
              <Scale className="w-4 h-4 transition-transform duration-200 group-hover:-rotate-12" style={{ color: GOLD }} /> Compare Money Tools
            </Link>
            <Link
              to="/features"
              className="group flex-1 inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full font-bold text-sm sm:text-base transition-all duration-200 hover:-translate-y-0.5 hover:shadow-xl shadow-md"
              style={{ background: INK, color: '#FFFFFF' }}
              data-testid="home-features-cta"
            >
              Explore Features
              <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ============ FREE SPRINT, THEN SEASON PASS ============ */}
      <section id="season-pass" className="scroll-mt-20" style={{ background: PAPER }} data-testid="home-pricing">
        <div className="max-w-5xl mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="lg:grid lg:grid-cols-[1.1fr_0.9fr] lg:gap-16 lg:items-start">

            {/* The offer */}
            <div>
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] mb-3" style={{ color: '#9A8F79' }} data-testid="pricing-eyebrow">
                Free 4-Relay Sprint
              </p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl mb-4 leading-tight" style={{ fontFamily: "'Fraunces', Georgia, serif", color: INK }} data-testid="pricing-heading">
                Try Four Relays Free
              </h2>
              <p className="text-base md:text-lg mb-8 leading-relaxed max-w-md" style={{ color: INK2 }} data-testid="pricing-subhead">
                Four weeks to find your weekly target, see what it needs to cover, and track your progress as you earn.
              </p>

              <ul className="space-y-3 mb-9 max-w-md" data-testid="pricing-features">
                {[
                  'A weekly target based on your real numbers',
                  'Income logging and progress in the app',
                  'A four-relay Google Sheet in your Drive',
                ].map((line) => (
                  <li key={line} className="flex items-start gap-3 text-sm sm:text-base" style={{ color: INK }}>
                    <span className="mt-2 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: GOLD }} />
                    <span>{line}</span>
                  </li>
                ))}
              </ul>

              <Link
                to="/start/free-sprint"
                className="group inline-flex items-center gap-2 px-8 py-4 rounded-full font-bold text-base transition-transform hover:scale-[1.02] shadow-md"
                style={{ background: GOLD, color: INK }}
                data-testid="pricing-sprint-link"
              >
                Start Your Free Sprint
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </Link>

              <p className="text-xs leading-relaxed mt-5 max-w-sm" style={{ color: '#8B8398' }} data-testid="pricing-sprint-note">
                $0 today — your 28-day Sprint is free. You add a card to start; it stays on file and your
                Season Pass begins automatically at day 28 ($19/mo, or annual) unless you cancel first.
              </p>
            </div>

            {/* Continue after your Sprint */}
            <div
              className="mt-14 lg:mt-0 rounded-3xl p-8 sm:p-9"
              style={{ background: INK, boxShadow: '0 28px 56px rgba(42,30,92,0.35), 0 10px 24px rgba(0,0,0,0.25)' }}
              data-testid="pricing-season-panel"
            >
              <h3 className="text-xl sm:text-2xl mb-6" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }} data-testid="pricing-season-heading">
                Start with full access today
              </h3>

              <div className="flex p-1 rounded-full mb-7" style={{ background: 'rgba(255,255,255,0.08)' }} data-testid="pricing-plan-toggle">
                {[['monthly', 'Monthly'], ['annual', 'Annual']].map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setPlan(id)}
                    className="flex-1 py-2.5 rounded-full text-sm font-bold transition-colors"
                    style={plan === id
                      ? { background: GOLD, color: INK }
                      : { background: 'transparent', color: 'rgba(255,255,255,0.7)' }}
                    data-testid={`pricing-plan-${id}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              <div className="flex items-baseline justify-center gap-2 mb-1" data-testid="pricing-amount">
                <span className="text-5xl" style={{ fontFamily: "'Fraunces', Georgia, serif", color: '#FFFFFF' }}>
                  {plan === 'annual' ? '$119' : '$19'}
                </span>
                <span className="text-sm" style={{ color: 'rgba(255,255,255,0.6)' }}>
                  {plan === 'annual' ? '/ year' : '/ month'}
                </span>
                {plan === 'annual' && (
                  <span className="ml-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-widest" style={{ background: GOLD, color: INK }} data-testid="pricing-save-badge">
                    Save 48%
                  </span>
                )}
              </div>
              <p className="text-sm text-center mb-8" style={{ color: 'rgba(255,255,255,0.6)' }} data-testid="pricing-same-system">
                Same full system either way. Cancel any time.
              </p>

              <Link
                to={plan === 'annual' ? '/start/season-pass?plan=annual' : '/start/season-pass'}
                className="block text-center px-6 py-3.5 rounded-full font-bold text-sm transition-transform hover:scale-[1.02]"
                style={{ background: '#FFFFFF', color: INK }}
                data-testid={plan === 'annual' ? 'pricing-annual-cta' : 'pricing-monthly-cta'}
              >
                Get Payday Relay
              </Link>

              <p className="flex items-center justify-center gap-2 text-xs mt-5" style={{ color: 'rgba(255,255,255,0.55)' }}>
                <Lock className="w-3.5 h-3.5" style={{ color: GOLD }} /> Secure checkout with Stripe.
              </p>
            </div>
          </div>

          <div className="mt-14 sm:mt-16 pt-10 border-t text-center max-w-2xl mx-auto" style={{ borderColor: 'rgba(42,30,92,0.12)' }} data-testid="pricing-beta-link-wrap">
            <p className="text-lg sm:text-xl leading-relaxed" style={{ color: INK2 }}>
              Want to help shape Payday Relay?{' '}
              <Link to="/beta-challenge" className="font-bold underline underline-offset-4 hover:opacity-80" style={{ color: INK }} data-testid="pricing-beta-challenge-link">
                Join the Four-Relay Beta Challenge &rarr;
              </Link>
            </p>
          </div>
        </div>
      </section>

      <QuoteWall />
      <SiteFooter />
    </div>
  );
}
